import { saveSiteForDap, saveSiteForMyProfile } from './axiosCDPFetcher';
import { store } from '../../store/store';
import {
  clearPendingSaveSite as clearPendingSaveSiteAction,
  setPendingSaveSite as setPendingSaveSiteAction,
} from '../../features/crProfileSlice';

type PendingSaveSite = {
  siteId: string;
  pageLink: string;
  siteName?: string | null;
};

export const getPendingSaveSite = (): PendingSaveSite | null => {
  return store.getState().crProfile?.pendingSaveSite || null;
};

export const setPendingSaveSite = (
  siteId: string,
  pageLink: string,
  siteName?: string | null
): void => {
  if (!siteId) {
    return;
  }

  store.dispatch(
    setPendingSaveSiteAction({
      siteId,
      pageLink,
      siteName: siteName ?? null,
    })
  );
};

export const clearPendingSaveSite = (): void => {
  store.dispatch(clearPendingSaveSiteAction());
};

export const getPreferredSiteIdForRegistration = (): string | null => {
  return getPendingSaveSite()?.siteId || null;
};

export const getPreferredSiteNameForRegistration = (): string | null => {
  return getPendingSaveSite()?.siteName || null;
};

/** CDP AddOrUpdateCrRegistrationDetails expects preferredSites as a string array. */
export const buildPreferredSitesForCdp = (siteId: string): string[] => [siteId];

/** DAP AddOrUpdateCRRegDetails expects preferredSites as a key/value collection. */
export const buildPreferredSitesForDap = (siteId: string, siteName?: string | null) => ({
  items: [{ id: siteId, siteName: siteName ?? null }],
  lastUpdated: new Date(),
});

/** Sitecore droplink item authored under Components/Save Site Modal. */
type SitecoreTextField = {
  value: string;
};

type SaveSiteContentFields = {
  modalTitle?: SitecoreTextField;
  modalDescription?: SitecoreTextField;
  inputText?: SitecoreTextField;
  [key: string]: any;
};

export type SaveSiteModalItem = {
  fields?: SaveSiteContentFields;
};

export type SaveSiteModalContent = {
  saveSiteModal?: SaveSiteModalItem;
  emailSent?: SaveSiteModalItem;
};

/**
 * Overlays authored Save Site modal fields onto My Profile modal fields so the shared modal can
 * be reused without hardcoded copy in client utilities.
 */
export const withSaveSiteModalContent = (profileFields: any, content?: SaveSiteModalContent) => {
  return {
    ...profileFields,
    myProfile: {
      ...profileFields?.myProfile,
      fields: {
        ...profileFields?.myProfile?.fields,
        ...content?.saveSiteModal?.fields,
      },
    },
    emailSent: {
      ...profileFields?.emailSent,
      fields: {
        ...profileFields?.emailSent?.fields,
        ...content?.emailSent?.fields,
      },
    },
  };
};

export const getPreferredSiteTokenFromQuery = (
  query: Record<string, string | string[] | undefined>
): string | undefined => {
  const token = query.preferredSitesToken || query.preferredSiteToken || query.siteToken;

  return typeof token === 'string' ? token : undefined;
};

const toSavedSiteIdList = (value: unknown): string[] | null => {
  if (Array.isArray(value)) {
    return value.map((site: any) => String(site?.siteId ?? site?.id ?? site?.text ?? site));
  }

  const items = (value as { items?: unknown })?.items;
  if (Array.isArray(items)) {
    return items.map((site: any) => String(site?.siteId ?? site?.id ?? site?.text ?? site));
  }

  return null;
};

const getSavedSiteIdCandidates = (profileData: any): unknown[] => [
  profileData?.siteIds,
  profileData?.SiteIds,
  profileData?.savedSites,
  profileData?.preferredSites,
  profileData?.dataExtension?.preferredSites,
];

export const getSavedSiteIds = (profileData: any): string[] => {
  for (const candidate of getSavedSiteIdCandidates(profileData)) {
    const ids = toSavedSiteIdList(candidate);
    if (ids) {
      return ids;
    }
  }
  return [];
};

export const hasSavedSiteIdsField = (profileData: any): boolean =>
  getSavedSiteIdCandidates(profileData).some(
    (candidate) =>
      Array.isArray(candidate) || Array.isArray((candidate as { items?: unknown })?.items)
  );

export const withUpdatedSavedSiteIds = (
  profileData: any,
  siteId: string,
  isSaved: boolean
): any => {
  const currentIds = getSavedSiteIds(profileData);
  const nextIds = isSaved
    ? Array.from(new Set([...currentIds, String(siteId)]))
    : currentIds.filter((id) => id !== String(siteId));

  return {
    ...profileData,
    siteIds: nextIds,
  };
};

export const savePreferredSite = async (
  token: string,
  siteId: string,
  siteName?: string | null
): Promise<void> => {
  if (!siteId) {
    return;
  }

  const payload = {
    myProfileToken: token,
    siteId,
    isSaved: true,
  };

  const savePromises: Promise<unknown>[] = [saveSiteForMyProfile(payload)];

  if (process.env.NEXT_PUBLIC_ALLOW_REGISTRATION_API_CALL === 'true') {
    savePromises.push(
      saveSiteForDap({
        ...payload,
        siteName: siteName ?? null,
      })
    );
  }

  await Promise.all(savePromises);
};
